import { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BRAND, BRAND_ASSETS } from '../config/programs';

const SESSION_KEY = 'autospa5-splash-played';
const HARD_TIMEOUT_MS = 3800;
const STAGE_TIMINGS = [
  { name: 'photo', at: 1500 },  // logo pulses 1.5s, then photo Ken-Burns
  { name: 'fade',  at: 3300 },  // start fade-out
  { name: 'done',  at: 3700 },
];

/**
 * Splash sequence (~3.5–3.8s):
 *   1) logo  (1.5s) — AutoSpa5 logo pulses with Tiffany contour halo
 *   2) photo (1.8s) — Auto + Portale photo, Ken-Burns slow zoom-in
 *   3) fade  (0.4s) — fade to the form
 *
 * The logo+halo backdrop is rendered as a STATIC sentinel layer (CSS-only animation)
 * so the splash never flashes a black frame, even while the photo asset (~2.6MB) is
 * still loading or framer-motion is mid-transition.
 */
export default function SplashIntro({ onDone, force = false }) {
  const [stage, setStage] = useState(() => {
    if (force) return 'logo';
    try {
      if (typeof window !== 'undefined' && window.sessionStorage.getItem(SESSION_KEY) === '1') {
        return 'done';
      }
    } catch (err) { console.debug('[Splash] sessionStorage unavailable', err?.message); }
    return 'logo';
  });
  const [photoReady, setPhotoReady] = useState(false);
  const photoReadyRef = useRef(false);

  // Preload the splash photo as soon as the component mounts so the Ken-Burns stage
  // doesn't show a blank black frame on first paint.
  useEffect(() => {
    const img = new Image();
    img.onload = () => { photoReadyRef.current = true; setPhotoReady(true); };
    img.onerror = () => { photoReadyRef.current = true; setPhotoReady(true); }; // fail-open
    img.src = BRAND_ASSETS.splashCarPortal;
  }, []);

  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (!force) {
      try { window.sessionStorage.setItem(SESSION_KEY, '1'); } catch (err) { console.debug('[Splash] sessionStorage unavailable', err?.message); }
    }
    setStage('done');
    onDoneRef.current?.();
  }, [force]);

  useEffect(() => {
    if (stage === 'done') {
      onDoneRef.current?.();
      doneRef.current = true;
      return undefined;
    }
    const timers = STAGE_TIMINGS.map(({ name, at }) =>
      setTimeout(() => {
        if (doneRef.current) return;
        if (name === 'done') finish();
        else if (name === 'photo') {
          // Hold on the logo stage until the photo is actually decoded — this prevents
          // the "black frame" gap on slow networks.
          const promote = () => { if (!doneRef.current) setStage('photo'); };
          if (photoReadyRef.current) promote();
          else {
            // Poll briefly (max 800ms) for the photo to be ready, then promote anyway.
            const start = Date.now();
            const iv = setInterval(() => {
              if (photoReadyRef.current || Date.now() - start > 800) {
                clearInterval(iv);
                promote();
              }
            }, 80);
          }
        } else setStage(name);
      }, at)
    );
    timers.push(setTimeout(finish, HARD_TIMEOUT_MS));
    return () => timers.forEach((t) => clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (stage === 'done') return null;

  const showLogoBackdrop = stage === 'logo' || (stage === 'photo' && !photoReady);

  return (
    <motion.div
      onClick={finish}
      onTouchStart={finish}
      className="fixed inset-0 z-50 bg-[#050505] flex items-center justify-center overflow-hidden cursor-pointer select-none"
      initial={{ opacity: 1 }}
      animate={{ opacity: stage === 'fade' ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="splash-intro"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); finish(); }}
        className="absolute top-6 right-6 z-30 text-[10px] uppercase tracking-[0.18em] text-white/40 hover:text-white/80 transition px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur"
        data-testid="splash-skip"
      >
        Skip
      </button>

      {/* Static logo+halo sentinel — pure CSS, no framer-motion. Always rendered while
          we're in 'logo' or while the photo is still loading during 'photo' stage,
          so the screen is never visually blank. */}
      {showLogoBackdrop && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none splash-logo-layer"
          data-testid={stage === 'logo' ? 'splash-logo' : 'splash-photo-fallback'}
        >
          <div
            className="absolute w-72 h-72 rounded-full blur-3xl"
            style={{
              background: `radial-gradient(circle, ${BRAND.primary} 0%, ${BRAND.primaryGlow} 45%, transparent 75%)`,
              opacity: 0.95,
              animation: 'splashHaloPulse 1.6s ease-in-out infinite',
            }}
          />
          <img
            src={BRAND_ASSETS.logo}
            alt=""
            className="w-40 h-40 relative z-10 object-contain"
            style={{
              filter:
                `drop-shadow(0 0 12px ${BRAND.primary}) ` +
                `drop-shadow(0 0 28px ${BRAND.primary}) ` +
                `drop-shadow(0 0 52px ${BRAND.primaryGlow})`,
              animation: 'splashLogoPulse 1.6s ease-in-out infinite',
            }}
          />
        </div>
      )}

      {/* Ken-Burns photo — only mounted once the asset has actually loaded. Subtle 1.0→1.08
          zoom over 2s keeps the wash portal visible at all times (previous 1.18 zoom was
          cropping the headlights and overhead arch). */}
      {(stage === 'photo' || stage === 'fade') && photoReady && (
        <motion.img
          key="photo"
          src={BRAND_ASSETS.splashCarPortal}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ opacity: 0, scale: 1.0 }}
          animate={{ opacity: 1, scale: 1.08 }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: 0.4 },
            scale: { duration: 2.6, ease: [0.22, 0.61, 0.36, 1] },
          }}
          data-testid="splash-photo"
        />
      )}

      {/* Subtle vignette to keep edges dark while photo zooms */}
      {(stage === 'photo' || stage === 'fade') && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 50% 60%, transparent 30%, rgba(5,5,5,0.55) 75%, #050505 100%)',
          }}
        />
      )}

      {/* Local keyframes so we don't depend on framer-motion completing its transitions
          (which can stall when keyframe arrays + repeat:Infinity are used). */}
      <style>{`
        @keyframes splashLogoPulse {
          0%, 100% { transform: scale(0.97); }
          50% { transform: scale(1.06); }
        }
        @keyframes splashHaloPulse {
          0%, 100% { opacity: 0.75; }
          50% { opacity: 1; }
        }
      `}</style>
    </motion.div>
  );
}
